import React, { Fragment, useState } from "react";

import type { Review, ReviewItem, Sir } from "./api";
import { mdBlocks, type Decision } from "./content";
import { MicIcon } from "./shell";
import { ItemAudio, type PlayKind } from "./Player";

// RV-6 — the Decision and Questions rooms. Same look and the same engine as Short form (a request
// holding items, each decided and noted); only how an item is shown and answered changes.
//   Decision: a SIR on one page (Situation, Information, Recommendation, what we need, appendices),
//             then Approve, Disapprove (needs a reason) or Ask a question (needs the question).
//   Questions: one card per call; the recommended answer and why, Take rec, your own answer
//             (typed or a voice note), Later; filters by group; take every remaining rec at once.

type Props = {
  review: Review; decisions: Record<string, Decision>; avatar: string; recording: boolean;
  onDecide: (itemId: string, d: Decision) => void; onMic: (itemId: string) => void;
  notesFor: (itemId: string) => React.ReactNode;
  // the room's read-along player: which audio is open, and play/stop an item's brief or read-aloud
  nowPlaying: string | null; onPlay: (it: ReviewItem, kind: PlayKind) => void;
  // a linked document room: how to open it from here (null = it can't be opened from this link)
  openDoc?: DocOpener;
  // deploy 9: "You are here" above the card's content, and options to choose between on it
  hereFor?: (x: ReviewItem) => React.ReactNode; optionsFor?: (x: ReviewItem) => React.ReactNode; callsFor?: (x: ReviewItem) => React.ReactNode;
};
export type DocLink = { id: string; label?: string; share?: string };
export type DocOpener = (d: DocLink) => (() => void) | null;
// "📄 Open the document": the item's question stays here; the document opens in its own Long form
// room to comment on, and Back returns to this room. Hidden when there's no way to open it.
export function DocButton({ doc, open }: { doc?: DocLink; open?: DocOpener }) {
  const go = doc && open ? open(doc) : null;
  if (!doc || !go) return null;
  return <button className="lr-docbtn" onClick={(e) => { e.stopPropagation(); go(); }}>📄 {doc.label || "Open the document"}</button>;
}
// the background a card needs before you can decide (item.context): shown open, above the question
const Context = ({ text }: { text?: string }) => (text ? <div className="lr-context"><Md text={text} /></div> : null);
const hasAudio = (x: ReviewItem) => !!(x.brief || x.readAudio);

// **bold** inside a line (the blocks come from mdBlocks: paragraphs, lists, tables)
function Inline({ text }: { text: string }) {
  return <>{text.split(/(\*\*[^*]+\*\*)/).map((p, i) => (/^\*\*[^*]+\*\*$/.test(p) ? <b key={i}>{p.slice(2, -2)}</b> : <Fragment key={i}>{p}</Fragment>))}</>;
}
export function Md({ text = "" }: { text?: string }) {
  return (
    <>
      {mdBlocks(text).map((b, i) =>
        b.kind === "table" ? (
          <div className="tbl" key={i}><table>
            <thead><tr>{b.head.map((c, k) => <th key={k}><Inline text={c} /></th>)}</tr></thead>
            <tbody>{b.rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}><Inline text={c} /></td>)}</tr>)}</tbody>
          </table></div>
        ) : b.kind === "ul" ? <ul key={i}>{b.lines.map((l, k) => <li key={k}><Inline text={l} /></li>)}</ul>
          : <p key={i}>{b.lines.map((l, k) => <Fragment key={k}>{k > 0 && <br />}<Inline text={l} /></Fragment>)}</p>,
      )}
    </>
  );
}

const list = (v?: string[] | string) => (Array.isArray(v) ? v : v ? [v] : []);

function SirPage({ sir }: { sir: Sir }) {
  const inf = sir.information || {}, rec = sir.recommendation || {};
  const asks = [...(sir.asks || []), ...(sir.needs_from_client || [])];
  return (
    <div className="lr-sir">
      {sir.situation && <section><h3>Situation</h3><Md text={sir.situation} /></section>}
      {(inf.accomplish || inf.impacted || inf.facts?.length || inf.options?.rows?.length || inf.risks?.length || inf.required_by) && (
        <section>
          <h3>Information</h3>
          {inf.accomplish && <p><b>What it should do:</b> <Inline text={inf.accomplish} /></p>}
          {inf.past_attempts && <p><b>Tried before:</b> <Inline text={inf.past_attempts} /></p>}
          {inf.root_cause && <p><b>The cause:</b> <Inline text={inf.root_cause} /></p>}
          {inf.impacted && <p><b>Who it affects:</b> <Inline text={inf.impacted} /></p>}
          {!!inf.facts?.length && <ul>{inf.facts.map((f, i) => <li key={i}><Inline text={f} /></li>)}</ul>}
          {!!inf.options?.rows?.length && (
            <div className="tbl">
              <table>
                {inf.options.columns && <thead><tr>{inf.options.columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>}
                <tbody>{inf.options.rows.map((r, i) => <tr key={i}>{r.map((c, k) => <td key={k}><Inline text={String(c)} /></td>)}</tr>)}</tbody>
              </table>
              {inf.options.summary && <p className="fine"><Inline text={inf.options.summary} /></p>}
            </div>
          )}
          {!!inf.risks?.length && <><p><b>What could go wrong:</b></p><ul>{inf.risks.map((r, i) => <li key={i}><Inline text={r.risk} />{r.handling && <> — <Inline text={r.handling} /></>}</li>)}</ul></>}
          {(list(inf.scope?.in).length > 0 || list(inf.scope?.out).length > 0) && <p><b>In scope:</b> {list(inf.scope?.in).join("; ") || "—"}<br /><b>Out of scope:</b> {list(inf.scope?.out).join("; ") || "—"}</p>}
          {inf.required_by && <p><b>Needed by:</b> <Inline text={inf.required_by} /></p>}
        </section>
      )}
      {(rec.pick || sir.next_step) && (
        <section className="rec">
          <h3>Recommendation</h3>
          {rec.pick && <Md text={rec.pick} />}
          {rec.how_it_solves && <Md text={rec.how_it_solves} />}
          {!!rec.why_not_others?.length && <><p><b>Why not the others:</b></p><ul>{rec.why_not_others.map((w, i) => <li key={i}><Inline text={w} /></li>)}</ul></>}
          {rec.cost && <p><b>Cost:</b> <Inline text={rec.cost} /></p>}
          {rec.implementation && <p><b>How it happens:</b> <Inline text={rec.implementation} /></p>}
          {rec.done_means && <p><b>Done means:</b> <Inline text={rec.done_means} /></p>}
          {!!rec.dates?.length && <ul>{rec.dates.map((d, i) => <li key={i}><Inline text={d} /></li>)}</ul>}
          {sir.next_step && <p><b>Next step:</b> <Inline text={sir.next_step} /></p>}
        </section>
      )}
      {!!asks.length && (
        <section>
          <h3>What we need</h3>
          <ul>{asks.map((a, i) => <li key={i}><Inline text={a.what} />{(a.who || a.by) && <span className="fine"> · {[a.who, a.by].filter(Boolean).join(", by ")}</span>}</li>)}</ul>
        </section>
      )}
      {!!sir.assumptions?.length && <section><h3>Assumptions</h3><ul>{sir.assumptions.map((a, i) => <li key={i}><Inline text={a} /></li>)}</ul></section>}
      {!!sir.appendices?.length && (
        <section>
          <h3>Appendices</h3>
          {sir.appendices.map((a, i) => (
            <details key={i}><summary>{a.letter ? `Appendix ${a.letter}. ` : ""}{a.title}</summary><Md text={a.body} /></details>
          ))}
        </section>
      )}
    </div>
  );
}

const DECISION_BADGE: Record<string, [string, string]> = { approve: ["Approved", "approved"], change: ["Disapproved", "changes"], question: ["Question asked", "look"] };

export function DecisionFlow({ review, decisions, avatar, recording, onDecide, onMic, notesFor, nowPlaying, onPlay, openDoc, hereFor, optionsFor, callsFor }: Props) {
  const [form, setForm] = useState<{ id: string; kind: "change" | "question"; text: string } | null>(null);
  return (
    <div className="lr-items">
      {review.items.map((x: ReviewItem, i) => {
        const d = decisions[x.id];
        const [badge, cls] = d ? DECISION_BADGE[d.decision] || ["Decided", "approved"] : ["Waiting on you", "look"];
        const asksProceed = x.sirType === "int";
        return (
          <section key={x.id} className={`lr-item active ${d ? "decided-keep" : ""}`} data-i={i} data-item={x.id}>
            <div className="lr-ihead static">
              <span className="lr-av">{avatar}</span>
              <span className="tx"><span className="t">{x.title || x.id}</span><span className="s2">{[review.client, x.kind, x.date].filter(Boolean).join(" · ")}</span></span>
              <span className={`lr-badge ${cls}`}>{badge}</span>
            </div>
            {hereFor?.(x)}
            {x.doc && <div className="lr-docrow"><DocButton doc={x.doc} open={openDoc} /></div>}
            {(hasAudio(x) || x.context) && (
              <div className="lr-dtop">
                {hasAudio(x) && <div className="lr-audiorow"><ItemAudio it={x} now={nowPlaying} onPlay={onPlay} /></div>}
                <Context text={x.context} />
              </div>
            )}
            {x.sir ? <SirPage sir={x.sir} /> : <div className="lr-sir"><Md text={x.details || x.rec || ""} /></div>}
            {optionsFor?.(x)}
            {callsFor?.(x)}
            <div style={{ padding: "0 16px" }}>{notesFor(x.id)}</div>
            <div className="lr-sign">
              {d && <div className="was">{badge}{d.note ? `: ${d.note}` : ""}</div>}
              {form?.id === x.id ? (
                <div className="lr-reason">
                  <label htmlFor={`dq-${x.id}`}>{form.kind === "question" ? "Your question" : "Why not?"}</label>
                  <textarea id={`dq-${x.id}`} autoFocus value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder={form.kind === "question" ? "e.g. What do the three banks pay now?" : "e.g. Wait until after the renewal"} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="cancel" onClick={() => setForm(null)}>Back</button>
                    <button className="send" disabled={!form.text.trim()} onClick={() => { onDecide(x.id, form.kind === "question" ? { decision: "question", note: form.text } : { decision: "change", reasons: ["Disapproved"], note: form.text }); setForm(null); }}>{form.kind === "question" ? "Send the question" : "Disapprove"}</button>
                  </div>
                </div>
              ) : (
                <div className="lr-acts">
                  <button className={`mic ${recording ? "rec" : ""}`} onClick={() => onMic(x.id)} aria-label={recording ? "Stop the voice note" : "Leave a voice note"}><MicIcon /></button>
                  <button className="ask" onClick={() => setForm({ id: x.id, kind: "question", text: "" })}>Ask a question</button>
                  <button className="chg" onClick={() => setForm({ id: x.id, kind: "change", text: "" })}>Disapprove</button>
                  <button className="ok" onClick={() => onDecide(x.id, { decision: "approve" })}>✓ {asksProceed ? "Yes, proceed" : "Approve"}</button>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function QuestionsFlow({ review, decisions, recording, onDecide, onMic, notesFor, nowPlaying, onPlay, openDoc, hereFor, optionsFor, callsFor }: Props) {
  const groups = (review.meta as { groups?: Record<string, string> }).groups || {};
  const [group, setGroup] = useState<string>("all");
  const [openOnly, setOpenOnly] = useState(false);
  const [later, setLater] = useState<Set<string>>(new Set());
  const [own, setOwn] = useState<{ id: string; text: string } | null>(null);
  const [details, setDetails] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState(false);
  const shown = review.items.filter((q) => (group === "all" || q.group === group) && (!openOnly || !decisions[q.id]));
  const remainingRecs = shown.filter((q) => q.rec && !decisions[q.id] && !later.has(q.id));
  const count = (g: string) => review.items.filter((q) => q.group === g).length;
  return (
    <div className="lr-items">
      <div className="lr-qbar">
        <div className="chips" role="group" aria-label="Show">
          <button aria-pressed={group === "all"} onClick={() => setGroup("all")}>All · {review.items.length}</button>
          {Object.entries(groups).map(([k, label]) => count(k) > 0 && <button key={k} aria-pressed={group === k} onClick={() => setGroup(k)}>{label} · {count(k)}</button>)}
        </div>
        <div className="row">
          <label><input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Unanswered only</label>
          {remainingRecs.length > 1 && (bulk ? (
            <span className="lr-confirm"><span>Take all {remainingRecs.length} recommendations shown?</span><button className="okb" onClick={() => { remainingRecs.forEach((q) => onDecide(q.id, { decision: "approve" })); setBulk(false); }}>Take them</button><button className="no" onClick={() => setBulk(false)}>Cancel</button></span>
          ) : <button className="lr-pill" onClick={() => setBulk(true)}>Take all {remainingRecs.length} remaining recs</button>)}
        </div>
      </div>
      {shown.map((q, i) => {
        const d = decisions[q.id];
        const isLater = later.has(q.id) && !d;
        return (
          <section key={q.id} className={`lr-item lr-q ${d ? "decided" : ""}`} data-i={i} data-item={q.id}>
            <div className="qhead"><span className="n">{q.id}</span><span className="g">{q.kind}</span>{d ? <span className={`lr-badge ${d.decision === "approve" ? "approved" : "ready"}`}>{d.decision === "approve" ? "Took rec" : "Answered"}</span> : isLater && <span className="lr-badge look">Later</span>}</div>
            {hereFor?.(q)}
            <Context text={q.context} />
            <h3 className="qt"><Inline text={q.title || q.id} /></h3>
            {!!q.choices?.length && <ul className="qchoices" aria-label="Choices">{q.choices.map((c) => <li key={c}><Inline text={c} /></li>)}</ul>}
            {q.rec && <div className="qrec"><b>Recommended:</b> <Inline text={q.rec} /></div>}
            {optionsFor?.(q)}
            {callsFor?.(q)}
            {q.doc && <div className="lr-docrow"><DocButton doc={q.doc} open={openDoc} /></div>}
            {hasAudio(q) && <div className="lr-audiorow"><ItemAudio it={q} now={nowPlaying} onPlay={onPlay} /></div>}
            {q.details && (
              <>
                <button className="qmore" aria-expanded={details.has(q.id)} onClick={() => { const n = new Set(details); n.has(q.id) ? n.delete(q.id) : n.add(q.id); setDetails(n); }}>{details.has(q.id) ? "Hide details" : "Details"}</button>
                {details.has(q.id) && <div className="qdetails"><Md text={q.details} /></div>}
              </>
            )}
            {notesFor(q.id)}
            {d && <div className="qanswer">{d.decision === "approve" ? `You took the recommendation.` : d.answer}</div>}
            {own?.id === q.id ? (
              <div className="lr-reason">
                <label htmlFor={`qa-${q.id}`}>Your answer</label>
                <textarea id={`qa-${q.id}`} autoFocus value={own.text} onChange={(e) => setOwn({ ...own, text: e.target.value })} placeholder="Say it the way you'd tell Claude" />
                <div style={{ display: "flex", gap: 8 }}>
                  <button className={`mic ${recording ? "rec" : ""}`} onClick={() => onMic(q.id)} aria-label={recording ? "Stop the voice note" : "Answer with a voice note"}><MicIcon /></button>
                  <button className="cancel" onClick={() => setOwn(null)}>Back</button>
                  <button className="send okb" disabled={!own.text.trim()} onClick={() => { onDecide(q.id, { decision: "answer", answer: own.text }); setOwn(null); }}>Save my answer</button>
                </div>
              </div>
            ) : (
              <div className="lr-acts qacts">
                {q.rec && <button className="ok" onClick={() => onDecide(q.id, { decision: "approve" })}>✓ Take rec</button>}
                <button className="own" onClick={() => setOwn({ id: q.id, text: d?.answer || "" })}>{d ? "Change my answer" : "My own answer"}</button>
                {!d && <button className="later" onClick={() => { const n = new Set(later); n.add(q.id); setLater(n); }}>Later</button>}
              </div>
            )}
          </section>
        );
      })}
      {!shown.length && <div className="lr-empty"><b>Nothing here.</b><span>{openOnly ? "Every question in this view is answered." : "No questions in this group."}</span></div>}
    </div>
  );
}
